import { Component, type ErrorInfo, type ReactNode } from "react"

import { AlertCircle, RotateCcw } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { logger } from "@/lib/logger"

import { SystemMessageLayout } from "./system-message-layout"

type Props = {
  children: ReactNode
  /** When this value changes (e.g. new answer reference), the boundary auto-resets. */
  resetKey?: unknown
}

type State = { hasError: boolean }

/**
 * Scoped boundary for a single rendered system message. Catches render/commit
 * errors (including DOM reconciliation failures from third-party DOM mutators)
 * so one bad message doesn't crash the whole page via the global boundary.
 */
export class SystemMessageBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    logger.error("SystemMessage render error", {
      error: { message: error.message, stack: error.stack, name: error.name },
      componentStack: info.componentStack,
    })
  }

  componentDidUpdate(prevProps: Props): void {
    const { hasError } = this.state
    const { resetKey } = this.props
    if (hasError && prevProps.resetKey !== resetKey) {
      this.setState({ hasError: false })
    }
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false })
  }

  render(): ReactNode {
    const { hasError } = this.state
    const { children } = this.props
    if (!hasError) {
      return children
    }

    return (
      <SystemMessageLayout>
        <SystemMessageLayout.Content>
          <div className="border-border/50 bg-muted/20 flex flex-col items-start gap-3 rounded-lg border p-4">
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <AlertCircle className="size-4" />
              <span>This message couldn't be rendered. Please retry.</span>
            </div>
            <Button size="sm" variant="outline" onClick={this.handleRetry}>
              <RotateCcw className="mr-2 size-3.5" />
              Retry
            </Button>
          </div>
        </SystemMessageLayout.Content>
      </SystemMessageLayout>
    )
  }
}
